import React, { useEffect, useMemo, useState } from "react";
import { Header } from "../components/ui/header";
import { money } from "../utilities/currency";
import axios from "axios";
import { Footer } from "../components/ui/footer";

type IconProps = { className?: string; title?: string };

function IconBase({ className, title, children }: IconProps & { children: React.ReactNode }) {
    return (
        <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className={className}
            role="img"
            aria-label={title}
        >
            {title ? <title>{title}</title> : null}
            {children}
        </svg>
    );
}

const Icons = {
    ShieldCheck: (p: IconProps) => (
        <IconBase {...p} title={p.title ?? "Sikkerhet"}>
            <path d="M12 2l7 4v6c0 5-3 9-7 10-4-1-7-5-7-10V6l7-4Z" />
            <path d="m9 12 2 2 4-4" />
        </IconBase>
    ),
    CreditCard: (p: IconProps) => (
        <IconBase {...p} title={p.title ?? "Kort"}>
            <rect x="3" y="5" width="18" height="14" rx="2" />
            <path d="M3 10h18" />
            <path d="M7 15h4" />
        </IconBase>
    ),
    Smartphone: (p: IconProps) => (
        <IconBase {...p} title={p.title ?? "Vipps"}>
            <rect x="7" y="2" width="10" height="20" rx="2" />
            <path d="M11 19h2" />
        </IconBase>
    ),
    Fingerprint: (p: IconProps) => (
        <IconBase {...p} title={p.title ?? "Verifisering"}>
            <path d="M12 11c0 3 0 7-2 9" />
            <path d="M12 11c0 4 0 9 2 11" />
            <path d="M8 11c0 3-1 6-3 8" />
            <path d="M16 11c0 3 1 6 3 8" />
            <path d="M7 7a7 7 0 0 1 10 0" />
            <path d="M5 9a9 9 0 0 1 14 0" />
        </IconBase>
    ),
    CalendarClock: (p: IconProps) => (
        <IconBase {...p} title={p.title ?? "Kalender"}>
            <rect x="3" y="4" width="18" height="16" rx="2" />
            <path d="M16 2v4M8 2v4M3 10h18" />
            <path d="M16.5 15.5l1.5 1.5" />
            <path d="M18 15a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
            <path d="M15 13.5V15l1 .5" />
        </IconBase>
    ),
    Receipt: (p: IconProps) => (
        <IconBase {...p} title={p.title ?? "Faktura"}>
            <path d="M6 2h12v20l-2-1-2 1-2-1-2 1-2-1-2 1V2Z" />
            <path d="M9 6h6M9 10h6M9 14h6" />
        </IconBase>
    ),
    Lock: (p: IconProps) => (
        <IconBase {...p} title={p.title ?? "Kryptering"}>
            <rect x="5" y="11" width="14" height="10" rx="2" />
            <path d="M8 11V8a4 4 0 0 1 8 0v3" />
        </IconBase>
    ),
    ArrowLeft: (p: IconProps) => (
        <IconBase {...p} title={p.title ?? "Tilbake"}>
            <path d="M19 12H5" />
            <path d="m12 19-7-7 7-7" />
        </IconBase>
    ),
    HelpCircle: (p: IconProps) => (
        <IconBase {...p} title={p.title ?? "Hjelp"}>
            <circle cx="12" cy="12" r="10" />
            <path d="M9.1 9a3 3 0 1 1 5.8 1c0 2-3 2-3 4" />
            <path d="M12 17h.01" />
        </IconBase>
    ),
    ChevronDown: (p: IconProps) => (
        <IconBase {...p} title={p.title ?? "Vis mer"}>
            <path d="m6 9 6 6 6-6" />
        </IconBase>
    ),
};

function cn(...classes: Array<string | false | null | undefined>) {
    return classes.filter(Boolean).join(" ");
}

function Pill({ active, children }: { active?: boolean; children: React.ReactNode }) {
    return (
        <span
            className={cn(
                "inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-medium",
                active
                    ? "border-orange-200 bg-orange-50 text-orange-700"
                    : "border-slate-200 bg-white text-slate-600"
            )}
        >
            {children}
        </span>
    );
}

function Card({
    title,
    icon,
    children,
    accent = "orange",
}: {
    title: string;
    icon: React.ReactNode;
    children: React.ReactNode;
    accent?: "orange" | "slate";
}) {
    const accentCls =
        accent === "orange"
            ? "from-orange-500/10 via-orange-500/5 to-transparent"
            : "from-slate-900/10 via-slate-900/5 to-transparent";

    return (
        <div className="relative overflow-hidden rounded-3xl border border-slate-200 bg-white p-6 shadow-[0_12px_35px_rgba(15,23,42,0.08)]">
            <div className={cn("pointer-events-none absolute inset-0 bg-gradient-to-br", accentCls)} />
            <div className="relative">
                <div className="flex items-start gap-4">
                    <div className="grid h-11 w-11 place-items-center rounded-2xl border border-slate-200 bg-white shadow-sm">
                        {icon}
                    </div>
                    <div className="min-w-0">
                        <h3 className="text-lg font-semibold text-slate-900">{title}</h3>
                    </div>
                </div>
                <div className="mt-4 space-y-3 text-sm leading-6 text-slate-700">{children}</div>
            </div>
        </div>
    );
}

function BadgeRow({
    items,
}: {
    items: Array<{ icon: React.ReactNode; label: string; active?: boolean }>;
}) {
    return (
        <div className="flex flex-wrap gap-2">
            {items.map((it, idx) => (
                <Pill key={idx} active={it.active}>
                    {it.icon}
                    {it.label}
                </Pill>
            ))}
        </div>
    );
}

function AccordionItem({ q, a }: { q: string; a: React.ReactNode }) {
    const [open, setOpen] = useState(false);
    return (
        <div className="rounded-2xl border border-slate-200 bg-white">
            <button
                onClick={() => setOpen((v) => !v)}
                className="flex w-full items-center justify-between gap-3 px-4 py-4 text-left"
                aria-expanded={open}
                type="button"
            >
                <span className="flex items-center gap-3">
                    <span className="grid h-9 w-9 place-items-center rounded-xl bg-slate-50 text-slate-700">
                        <Icons.HelpCircle className="h-5 w-5" />
                    </span>
                    <span className="font-medium text-slate-900">{q}</span>
                </span>
                <Icons.ChevronDown className={cn("h-5 w-5 text-slate-500 transition", open && "rotate-180")} />
            </button>
            {open ? <div className="px-4 pb-4 text-sm leading-6 text-slate-700">{a}</div> : null}
        </div>
    );
}

type PaymentMethodsPageProps = {
    currentUser: any,
    onLogout: any,
    baseUrl: string,
    onLoginClick: any,
    onLoginSuccess: any,
}
export default function PaymentMethodsPage({ currentUser, onLogout, baseUrl, onLoginClick, onLoginSuccess }: PaymentMethodsPageProps) {
    const [wallet, setWallet] = useState(0);
    const [scrolled, setScrolled] = useState(false);

    const [method, setMethod] = useState<"vipps" | "kort">("vipps");

    const T = {
        offer: "✨ Spesialtilbud – Betal 99 kr, få 198 kr i ringe kreditt! ✨",
        signIn: "Logg inn",
        join: "Bli med",
        allAdvisors: "Alle veiledere",
        festivalTitle: "Festivalkampanjer",
        referTitle: "Verv & få gratis reading!",
        referBody: "Verv 5 betalende kunder og få en GRATIS 15‑min reading hos en toppveileder.",
        blogTitle: "Fra Mystic‑bloggen",
        viewAll: "Se alle",
        subscribeLine: "Få nye guider og tilbud på e‑post.",
        read: "Les",
        invite: "Inviter venner",
        sponsored: "Sponset",
        ad: (n: number) => `Annonse ${n}`,
        adBlurb: "Annonser produkter, apper eller arrangementer her.",
        learnMore: "Les mer",
        connectNow: "Koble til nå",
        from: "fra",
        minutes: "min",
        verified: "Verifisert",
        categories: [
            "Kjærlighetsreading",
            "Klarsynt reading",
            "Tarot‑reading",
            "Astrologi & Horoskop",
            "Englebudskap",
            "Orakel‑veiledning",
            "Drømmetydning",
            "Åndelig medium",
        ],
        hero: {
            name: "Klarsynte Eirik",
            tag: "Kjærlighetsekspert",
            blurb:
                "Mitt løfte: Jeg er her for deg. Når det er vanskelig å vite hvem man kan stole på, er ekte nærvær alt.",
            getReading: "Bestill reading",
        },
        footer: {
            getApp: "Få appen for klarhet hvor du enn er.",
            explore: "Utforsk",
            special: "Spesialiteter",
            more: "Mer",
            advisors: "Veiledere",
            blog: "Bloggen vår",
            applyCode: "Bruk kampanjekode",
            support: "Kundesupport",
            about: "Om oss",
            apply: "Veileder? Søk her",
            privacy: "Personvern",
            terms: "Vilkår",
        },
        wallet: {
            title: "Kjøp ringeminutter",
            intro:
                "Minutter lagres i din Mystic‑wallet og kan brukes hos alle veiledere (chat / samtale / video).",
            numLabel: "Telefonnummeret du ringer fra",
            amount: "Velg beløp",
            buy: (amt: number) => `Kjøp ${money(amt)} kreditt`,
            learn: "Les mer om betalingsmåter",
            err: "Skriv inn et gyldig telefonnummer",
            otp: (n: string) => `Kode sendt til ${n}. Skriv den inn for å bekrefte.`,
            enterOtp: "Skriv inn kode",
            verify: "Bekreft & legg til kreditt",
            cancel: "Avbryt",
            success: (amt: number) => `${money(amt)} lagt til i lommeboken!`,
            pm1: "Vipps MobilePay",
            pm2: "Kort (krever registrering på ditt mobilnummer)",
            nolgin: "Ingen innlogging nødvendig for Vipps MobilePay – ring direkte.",
        },
    };

    const highlights = useMemo(
        () => [
            {
                icon: <Icons.ShieldCheck className="h-5 w-5 text-orange-600" />,
                label: "Sikre betalingsmåter",
                active: true,
            },
            {
                icon: <Icons.Fingerprint className="h-5 w-5 text-slate-700" />,
                label: "BankID / Vipps-login",
            },
            {
                icon: <Icons.Lock className="h-5 w-5 text-slate-700" />,
                label: "Kryptert betaling",
            },
            {
                icon: <Icons.Receipt className="h-5 w-5 text-slate-700" />,
                label: "Faktura (hvis aktivert)",
            },
        ],
        []
    );

    useEffect(() => {
        if (currentUser?.role === "customer") {
            axios.get(baseUrl + "/getCustomerWallet", { params: { token: currentUser.token } })

                .then((response) => {
                    if (response.data.status === "OK") {
                        setWallet(response.data.data.remaining_amount);
                    }
                });
        }
    }, [currentUser, baseUrl])
    return (
        <div className={"bg-white text-gray-900"}>
            <Header wallet={wallet} scrolled={scrolled} T={T} onLoginClick={onLoginClick} currentUser={currentUser} onLogout={onLogout} baseUrl={baseUrl} />
            <main className="min-h-screen bg-gray-50 text-gray-800 py-16 px-6 md:px-12 lg:px-24">

                {/* Hero */}
                <div className="mx-auto max-w-6xl px-5 pb-10 pt-8">
                    <div className="grid items-stretch gap-6 lg:grid-cols-2">
                        <div className="rounded-[28px] border border-slate-200 bg-white p-7 shadow-[0_14px_40px_rgba(15,23,42,0.08)]">
                            <div className="inline-flex items-center gap-2 rounded-full bg-orange-50 px-3 py-1 text-xs font-semibold text-orange-700">
                                <Icons.ShieldCheck className="h-4 w-4" />
                                Trygge betalingsløsninger
                            </div>
                            <h1 className="mt-4 text-3xl font-semibold tracking-tight text-slate-900">
                                Les mer om betalingsmåter
                            </h1>
                            <p className="mt-3 text-sm leading-6 text-slate-700">
                                Teletorget er et demo nettsted for CHAMCORE systemene og støtter sikre betalingsflyter for
                                ringeminutter, avtaler med veileder og kurs. Du kan velge Vipps eller
                                kortbetaling via Stripe — begge med verifisert og trygg registrering.
                            </p>

                            <div className="mt-5">
                                <BadgeRow items={highlights} />
                            </div>

                            {/* Quick selector */}
                            <div className="mt-6 rounded-3xl border border-slate-200 bg-slate-50 p-3">
                                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                                    <div>
                                        <div className="text-xs font-semibold text-slate-700">Velg betalingsmåte</div>
                                        <div className="text-xs text-slate-500">Bytt for å se detaljer</div>
                                    </div>
                                    <div className="flex gap-2">
                                        <button
                                            onClick={() => setMethod("vipps")}
                                            className={cn(
                                                "inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition",
                                                method === "vipps"
                                                    ? "bg-orange-600 text-white shadow"
                                                    : "bg-white text-slate-700 hover:bg-slate-100"
                                            )}
                                            type="button"
                                        >
                                            <Icons.Smartphone className="h-4 w-4" />
                                            Vipps
                                        </button>
                                        <button
                                            onClick={() => setMethod("kort")}
                                            className={cn(
                                                "inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition",
                                                method === "kort"
                                                    ? "bg-orange-600 text-white shadow"
                                                    : "bg-white text-slate-700 hover:bg-slate-100"
                                            )}
                                            type="button"
                                        >
                                            <Icons.CreditCard className="h-4 w-4" />
                                            Kort
                                        </button>
                                    </div>
                                </div>

                                <div className="mt-4 rounded-3xl border border-slate-200 bg-white p-5">
                                    {method === "vipps" ? (
                                        <div className="space-y-2 text-sm leading-6 text-slate-700">
                                            <div className="flex items-center gap-2 font-semibold text-slate-900">
                                                <Icons.Smartphone className="h-5 w-5 text-orange-600" />
                                                Vipps (Nordisk betalingsformidler)
                                            </div>
                                            <p>
                                                Vipps gir en enkel og trygg betalingsopplevelse. Du kan ringe med din telefon, registrere deg og
                                                logge inn med Vipps/BankID-støtte, kjøpe ringeminutter, og bestille
                                                avtaler med veileder via bookingkalender.
                                            </p>
                                            <ul className="mt-2 list-disc space-y-1 pl-5">
                                                <li>
                                                    <span className="font-medium text-slate-900">Registrering og innlogging:</span> skjer via Vipps, med høy
                                                    sikkerhet.
                                                </li>
                                                <li>
                                                    <span className="font-medium text-slate-900">Kontroll og samtykker:</span> du administrerer tilgang og
                                                    samtykker direkte i Vipps-appen.
                                                </li>
                                                <li>
                                                    <span className="font-medium text-slate-900">Bruk på tvers:</span> kjøpte ringeminutter kan brukes på
                                                    hvilken som helst tjeneste/veileder.
                                                </li>
                                            </ul>
                                        </div>
                                    ) : (
                                        <div className="space-y-2 text-sm leading-6 text-slate-700">
                                            <div className="flex items-center gap-2 font-semibold text-slate-900">
                                                <Icons.CreditCard className="h-5 w-5 text-orange-600" />
                                                Kortbetaling via Stripe
                                            </div>
                                            <p>
                                                Kortbetaling behandles av Stripe. Etter registrering kan du lagre
                                                betalingskortet ditt hos Stripe (sikkert og kryptert), slik at fremtidige
                                                samtaler, avtaler og kurs kan belastes uten at du må taste inn kortinformasjon på nytt.
                                            </p>
                                            <ul className="mt-2 list-disc space-y-1 pl-5">
                                                <li>
                                                    <span className="font-medium text-slate-900">Sikker kortlagring:</span> kortinfo lagres hos Stripe — ikke
                                                    på Teletorget.
                                                </li>
                                                <li>
                                                    <span className="font-medium text-slate-900">Enklere fremtidige kjøp:</span> belastninger kan skje raskt
                                                    når du bestiller nye avtaler eller kurs.
                                                </li>
                                                <li>
                                                    <span className="font-medium text-slate-900">Støtte for forhåndsautorisasjon:</span> om aktivert, kan beløp
                                                    reserveres og endelig beløp trekkes etter faktisk forbruk.
                                                </li>
                                            </ul>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* CTA row */}
                            <div className="mt-6 flex flex-col gap-3 sm:flex-row">
                                <a
                                    href="#faq"
                                    className="inline-flex items-center justify-center rounded-full bg-slate-900 px-5 py-3 text-sm font-semibold text-white hover:opacity-95"
                                >
                                    Se vanlige spørsmål
                                </a>
                                <a
                                    href="#sikkerhet"
                                    className="inline-flex items-center justify-center rounded-full border border-orange-200 bg-orange-50 px-5 py-3 text-sm font-semibold text-orange-700 hover:bg-orange-100"
                                >
                                    Hvordan vi sikrer betaling
                                </a>
                            </div>
                        </div>

                        {/* Right visual */}
                        <div className="relative overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-[0_14px_40px_rgba(15,23,42,0.08)]">
                            <div className="absolute inset-0 bg-gradient-to-br from-slate-900/10 via-orange-500/10 to-transparent" />
                            <div className="relative grid h-full place-items-center p-8">
                                <div className="w-full max-w-md">
                                    <div className="rounded-[26px] border border-slate-200 bg-white p-6 shadow-sm">
                                        <div className="flex items-start justify-between gap-4">
                                            <div>
                                                <div className="text-xs font-semibold text-slate-700">Betalingsflyt</div>
                                                <div className="mt-1 text-lg font-semibold text-slate-900">Kjøp ringeminutter</div>
                                                <div className="mt-1 text-sm text-slate-600">
                                                    Bruk dem på hvilken som helst tjeneste og veileder
                                                </div>
                                            </div>
                                            <div className="grid h-11 w-11 place-items-center rounded-2xl bg-orange-50 text-orange-700">
                                                <Icons.CalendarClock className="h-5 w-5" />
                                            </div>
                                        </div>

                                        <div className="mt-5 space-y-3">
                                            <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
                                                <div className="text-xs font-semibold text-slate-700">Telefonnummeret du ringer fra</div>
                                                <div className="mt-1 text-sm text-slate-500">+47 • Ditt mobilnummer</div>
                                            </div>

                                            <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3">
                                                <div className="text-xs font-semibold text-slate-700">Velg beløp</div>
                                                <div className="mt-3 flex flex-wrap gap-2">
                                                    {[
                                                        { v: "149 kr", active: false },
                                                        { v: "399 kr", active: true },
                                                        { v: "999 kr", active: false },
                                                    ].map((x) => (
                                                        <span
                                                            key={x.v}
                                                            className={cn(
                                                                "inline-flex items-center rounded-full border px-4 py-2 text-sm font-semibold",
                                                                x.active
                                                                    ? "border-orange-200 bg-orange-50 text-orange-700"
                                                                    : "border-slate-200 bg-white text-slate-700"
                                                            )}
                                                        >
                                                            {x.v}
                                                        </span>
                                                    ))}
                                                </div>

                                                <div className="mt-4 flex flex-wrap gap-2">
                                                    <span className="inline-flex items-center gap-2 rounded-full border border-orange-200 bg-orange-600 px-4 py-2 text-sm font-semibold text-white shadow">
                                                        <Icons.Smartphone className="h-4 w-4" />
                                                        Vipps MobilePay
                                                    </span>
                                                    <span className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700">
                                                        <Icons.CreditCard className="h-4 w-4" />
                                                        Kort
                                                    </span>
                                                </div>
                                            </div>

                                            <button className="w-full rounded-full bg-orange-600 px-5 py-3 text-sm font-semibold text-white shadow hover:opacity-95" type="button">
                                                Kjøp 399 kr kreditt
                                            </button>
                                            <div className="text-center text-xs text-slate-500">
                                                Trykk «Les mer» for detaljer om betalingsmåter.
                                            </div>
                                        </div>
                                    </div>

                                    <div className="mt-5 rounded-[26px] border border-slate-200 bg-white p-5">
                                        <div className="flex items-start gap-3">
                                            <div className="grid h-10 w-10 place-items-center rounded-2xl bg-slate-50 text-slate-700">
                                                <Icons.Fingerprint className="h-5 w-5" />
                                            </div>
                                            <div>
                                                <div className="text-sm font-semibold text-slate-900">Verifisert bruker</div>
                                                <div className="mt-1 text-sm leading-6 text-slate-700">
                                                    CHAMCORE støtter sikker onboarding via BankID eller Vipps, avhengig av
                                                    valgt betalingsleverandør.
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Main content */}
                    <div className="mt-8 grid gap-6 lg:grid-cols-3">
                        <Card
                            title="Vipps – nordisk, raskt og brukervennlig"
                            icon={<Icons.Smartphone className="h-5 w-5 text-orange-600" />}
                            accent="orange"
                        >
                            <p>
                                Vipps er en utbredt nordisk betalingsformidler som lar deg betale med et par trykk.
                                Når du bruker Vipps kan du også registrere deg og logge inn, og du kan bestille
                                avtaler med veileder i vår bookingkalender.
                            </p>
                            <p>
                                Du styrer alltid hvilke samtykker du har gitt, og administrerer tilganger i Vipps-appen.
                            </p>
                        </Card>

                        <Card
                            title="Kort via Stripe – fleksibelt for kurs og fremtidige kjøp"
                            icon={<Icons.CreditCard className="h-5 w-5 text-orange-600" />}
                            accent="orange"
                        >
                            <p>
                                Kortbetaling håndteres av Stripe. Etter registrering kan du lagre betalingsinformasjonen
                                din hos Stripe slik at vi kan belaste fremtidige avtaler og kurs raskt og sikkert.
                            </p>
                            <p>
                                Kortinformasjon lagres hos Stripe (ikke på Teletorget), og betaling skjer med kryptert
                                kommunikasjon.
                            </p>
                        </Card>

                        <Card
                            title="Sikkerhet – BankID, Vipps-login og forhåndsautorisasjon"
                            icon={<Icons.ShieldCheck className="h-5 w-5 text-orange-600" />}
                            accent="slate"
                        >
                            <p id="sikkerhet">
                                CHAMCORE sine systemer støtter sikker betaling. Du må derfor registrere deg med BankID
                                eller Vipps gjennom de tilgjengelige betalingsleverandørene.
                            </p>
                            <p>
                                Dersom fakturering er aktivert, støttes dette også av Vipps og Stripe.
                                Forhåndsautorisasjon kan alltid benyttes når det er aktivert — slik at beløp kan
                                reserveres, og endelig beløp trekkes når tjenesten er levert.
                            </p>
                        </Card>
                    </div>

                    {/* FAQ */}
                    <div id="faq" className="mt-10">
                        <div className="flex items-end justify-between gap-4">
                            <div>
                                <div className="inline-flex items-center gap-2 rounded-full bg-slate-900 px-3 py-1 text-xs font-semibold text-white">
                                    <Icons.HelpCircle className="h-4 w-4" />
                                    FAQ
                                </div>
                                <h2 className="mt-3 text-2xl font-semibold tracking-tight text-slate-900">Vanlige spørsmål</h2>
                                <p className="mt-2 text-sm text-slate-600">Her er korte svar på det kunder oftest lurer på.</p>
                            </div>
                        </div>

                        <div className="mt-5 grid gap-3 lg:grid-cols-2">
                            <AccordionItem
                                q="Hvorfor må jeg registrere meg med BankID eller Vipps?"
                                a={
                                    <>
                                        For å sikre trygg betaling, redusere misbruk og gi deg en sømløs opplevelse ved
                                        bestilling av avtaler og kurs. Verifisering skjer via betalingsleverandørene
                                        (Vipps/Stripe) og er integrert i CHAMCORE-plattformen.
                                    </>
                                }
                            />
                            <AccordionItem
                                q="Lagrer Teletorget kortinformasjonen min?"
                                a={
                                    <>
                                        Nei. Ved kortbetaling lagres kortinformasjon hos Stripe. Teletorget mottar kun en
                                        sikker betalingsreferanse som brukes til å gjennomføre fremtidige belastninger når
                                        du bestiller nye avtaler eller kurs.
                                    </>
                                }
                            />
                            <AccordionItem
                                q="Hva er forhåndsautorisasjon, og når brukes det?"
                                a={
                                    <>
                                        Forhåndsautorisasjon betyr at et beløp kan reserveres på forhånd, og at endelig
                                        trekk justeres etter faktisk forbruk (for eksempel ved takserte tjenester). Dette
                                        kan benyttes når funksjonen er aktivert.
                                    </>
                                }
                            />
                            <AccordionItem
                                q="Kan jeg få faktura?"
                                a={
                                    <>
                                        Dersom fakturering er aktivert for din tjeneste/avtale, støttes dette av både Vipps
                                        og Stripe. Du vil se fakturaalternativet i bestillingsflyten når det er tilgjengelig.
                                    </>
                                }
                            />
                        </div>
                    </div>

                    {/* Footer */}
                    <div className="mt-10 rounded-3xl border border-slate-200 bg-white p-6 shadow-[0_12px_35px_rgba(15,23,42,0.06)]">
                        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                            <div className="flex items-center gap-3">
                                <div className="grid h-11 w-11 place-items-center rounded-2xl bg-orange-50 text-orange-700">
                                    <Icons.ShieldCheck className="h-5 w-5" />
                                </div>
                                <div>
                                    <div className="text-sm font-semibold text-slate-900">Trygg betaling med CHAMCORE</div>
                                    <div className="text-sm text-slate-600">Vipps • Stripe • BankID/Vipps-login • Faktura (hvis aktivert)</div>
                                </div>
                            </div>

                            <button
                                className="inline-flex items-center justify-center rounded-full bg-orange-600 px-5 py-3 text-sm font-semibold text-white shadow hover:opacity-95"
                                onClick={() => window.history.back()}
                                type="button"
                            >
                                Tilbake til kjøp
                            </button>
                        </div>
                        <div className="mt-4 text-xs text-slate-500">© {new Date().getFullYear()} Teletorget • Powered by CHAMCORE Technologies</div>
                    </div>
                </div>
            </main>
            <Footer baseUrl={baseUrl} />
        </div>
    );
}
